import { Icon20MessageOutline, Icon20ThumbsDown, Icon20ThumbsDownOutline, Icon20ThumbsUp, Icon20ThumbsUpOutline } from '@vkontakte/icons';
import { Spacing, Text, Textarea } from '@vkontakte/vkui';
import classNames from 'clsx';
import React, { type RefObject, memo, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import type { ProcessingType } from 'reactApp/modules/aiTools/aiTools.types';
import { sendDwhButton, sendDwhTooltip } from 'reactApp/modules/dwh/dwh.helpers';
import { showSnackbarAction } from 'reactApp/modules/snackbar/snackbar.actions';
import { SnackbarTypes } from 'reactApp/modules/snackbar/snackbar.types';
import { Button, ButtonSizeMode } from 'reactApp/ui/Button/Button';
import { FloatingTooltip } from 'reactApp/ui/FloatingTooltip/FloatingTooltip';
import { ETooltipAppearance, ETooltipPlacement } from 'reactApp/ui/FloatingTooltip/FloatingTooltip.types';
import { Delimiter } from 'reactApp/ui/ReactViewer/AiImprove/Buttons';

import styles from './AiImproveToolbar.css';

const MAX_SYMBOLS = 256;
const MESSAGE_SHOW_DELAY = 300;
const MIN_MESSAGE_LEN = 3;

type RepsonseState = 'show' | 'message' | 'done' | null;

export const RatingBlock = memo<{ type?: ProcessingType }>(function RatingBlock({ type }) {
    const ref = useRef<HTMLDivElement>(null);
    const dispatch = useDispatch();
    const [rating, setRating] = useState<boolean | null>(null);
    const [isError, setIsError] = useState<boolean>(false);
    const [message, setMessage] = useState<string>('');
    const [responseState, setResponseState] = useState<RepsonseState>(null);

    const isRatingUp = rating === true;
    const isRatingDown = rating === false;
    const isResponseButton = responseState === 'show';
    const isEditMessage = responseState === 'message';
    const isResponseSent = responseState === 'done';

    const name = type === 'improve' ? 'mnz_ai_improve_rating' : 'mnz_ai_rmvbgrnd_rating';

    useEffect(() => {
        sendDwhButton({ name, button_name: 'rating' });
    }, [name]);

    useEffect(() => {
        if (isEditMessage) {
            sendDwhTooltip({ name });
        }
    }, [isEditMessage, name]);

    useEffect(() => {
        let timerId;
        if (isRatingDown || isRatingUp) {
            timerId = setTimeout(() => setResponseState('show'), MESSAGE_SHOW_DELAY);
        }

        return () => {
            if (timerId) {
                clearTimeout(timerId);
            }
        };
    }, [isRatingUp, isRatingDown]);

    const handleUp = useCallback(() => {
        if (isRatingDown) {
            return;
        }

        setRating(true);
        sendDwhButton({ action: 'click', name, button_name: 'rating_up' });
    }, [isRatingDown, name]);

    const handleDown = useCallback(() => {
        if (isRatingUp) {
            return;
        }

        setRating(false);
        sendDwhButton({ action: 'click', name, button_name: 'rating_down' });
    }, [isRatingUp, name]);

    const showMessageClick = useCallback(() => {
        if (isEditMessage) {
            return;
        }

        setResponseState('message');
        sendDwhButton({ name, action: 'click', button_name: 'message' });
    }, [isEditMessage, name]);

    const sendMessageClick = useCallback(() => {
        if (message.length < MIN_MESSAGE_LEN) {
            dispatch(
                showSnackbarAction({
                    id: 'ai-improve-rating-fail',
                    type: SnackbarTypes.failure,
                    text: 'Интересно ваше мнение — поделитесь, пожалуйста',
                    closable: true,
                })
            );
            setIsError(true);
            return;
        }

        setResponseState('done');
        sendDwhTooltip({
            action: 'click',
            name,
            button_name: 'send_message',
            data: { message: message.slice(0, MAX_SYMBOLS), rating: isRatingUp ? 'up' : 'down' },
        });
        dispatch(
            showSnackbarAction({
                id: 'ai-improve-rating-success',
                type: SnackbarTypes.success,
                text: 'Спасибо за ваше мнение',
                autoWidth: true,
                closable: true,
            })
        );
    }, [message, name, isRatingUp, dispatch]);

    const onChange = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setMessage(event.target.value);
        setIsError(false);
    }, []);

    const onEnterPress = useCallback(
        (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
            if (event.code === 'Enter') {
                event.preventDefault();

                sendMessageClick();
            }
        },
        [sendMessageClick]
    );

    const onTooltipClose = useCallback(() => {
        sendDwhTooltip({ name, action: 'close' });
        setMessage('');
        setIsError(false);
        setResponseState('show');
    }, [name]);

    if (isResponseSent) {
        return <div className={classNames(styles.ratingWrapper, styles.ratingWrapper_none)} />;
    }

    if (isResponseButton || isEditMessage) {
        return (
            <>
                <div className={classNames(styles.ratingWrapper, styles.ratingWrapper_long)}>
                    <div
                        className={classNames(styles.button, { [styles.button_active]: isEditMessage })}
                        onClick={showMessageClick}
                        data-qa-id="send-message-bttn"
                        ref={ref}
                    >
                        <Icon20MessageOutline data-qa-id="message-icon" />
                        Оставьте отзыв
                    </div>
                    {isEditMessage && (
                        <FloatingTooltip
                            onClose={onTooltipClose}
                            onCrossClose={() => setResponseState('show')}
                            target={ref as RefObject<HTMLDivElement>}
                            placement={ETooltipPlacement.topStart}
                            qaId="send-message-ai-improve"
                            appearance={ETooltipAppearance.dark}
                            className={styles.tooltipRoot}
                            leftOffset={-82}
                            closable
                            usePortal
                            hideArrow
                            closeOnOutsideClick={true}
                            autoUpdateOnResize
                            allowFlip
                        >
                            <div className={styles.tooltip}>
                                <Text className={styles.tooltipText}>
                                    {type === 'improve' ? 'Как вам улучшение фото' : 'Как вам удаление фона'}
                                    <br />с нейросетью?
                                </Text>
                                <Spacing size={16} />
                                <Textarea
                                    data-qa-id="ai-resp-text"
                                    className={styles.tooltipEdit}
                                    placeholder="Напишите, что понравилось, а что можно улучшить"
                                    value={message}
                                    onChange={onChange}
                                    onKeyPress={onEnterPress}
                                    rows={3}
                                    maxHeight={80}
                                    maxLength={MAX_SYMBOLS}
                                    status={isError ? 'error' : undefined}
                                />
                                <Spacing size={8} />
                                <div className={styles.counter}>
                                    {message.length}/{MAX_SYMBOLS}
                                </div>
                                <Spacing size={20} />
                                <Button
                                    theme="vk"
                                    sizeMode={ButtonSizeMode.small}
                                    className={styles.tooltipButton}
                                    onClick={sendMessageClick}
                                    primary
                                >
                                    Отправить
                                </Button>
                            </div>
                        </FloatingTooltip>
                    )}
                </div>
                <Delimiter />
            </>
        );
    }

    return (
        <>
            <div className={styles.ratingWrapper}>
                <div className={styles.ratingBlock}>
                    <div
                        className={classNames(styles.button, styles.button_icon, { [styles.button_active]: isRatingUp })}
                        data-qa-id="rating-block"
                        onClick={handleUp}
                    >
                        <Icon20ThumbsUpOutline
                            data-qa-id="rating-up"
                            className={classNames(styles.icon, { [styles.iconActive]: !isRatingUp })}
                        />
                        <Icon20ThumbsUp
                            data-qa-id="rating-up-active"
                            className={classNames(styles.icon, { [styles.iconActive]: isRatingUp })}
                        />
                    </div>
                    <div
                        className={classNames(styles.button, styles.button_icon, { [styles.button_active]: isRatingDown })}
                        data-qa-id="rating-block"
                        onClick={handleDown}
                    >
                        <Icon20ThumbsDownOutline
                            data-qa-id="rating-down"
                            className={classNames(styles.icon, { [styles.iconActive]: !isRatingDown })}
                        />
                        <Icon20ThumbsDown
                            data-qa-id="rating-down-active"
                            className={classNames(styles.icon, { [styles.iconActive]: isRatingDown })}
                        />
                    </div>
                </div>
            </div>
            <Delimiter />
        </>
    );
});

RatingBlock.displayName = 'RatingBlock';
